import { useAllPages, usePages } from 'hooks';
import { useEffect, useState } from 'react';
import { WPAcfBasic } from 'types/acf';
import { LanguageTypes } from 'types/global';
import { WPSimplePage } from 'types/pages';
import config from 'config/config';
import isEqual from 'lodash-es/isEqual';
import isEmpty from 'lodash-es/isEmpty';
import { WPSimpleArticle } from 'types/articles';

export const getAcceptablePostRoutes = () => (window && window.globalCache && !isEmpty(window.globalCache.postRoutes) ? window.globalCache.postRoutes : null);
export const hasAcceptablePostRoutes = () => !!Object.keys(window?.globalCache?.postRoutes || {}).length;

interface CachedDataProps {
  languageRoutes: Record<string, Record<string, string>> | null;
  pages: WPSimplePage<WPAcfBasic>[] | null;
}

const getLanguages = () => {
  const languages = {};
  config.languages.forEach(language => {
    // @ts-ignore
    languages[language] = {};
  });
  return languages as Record<LanguageTypes, Record<string, string>>;
};

const getAdditionalLanguageSlugs = () => {
  return {
    'blog/:articleId': 'blog/:articleId',
    'preview/:parentId/:revision/:preview': 'preview/:parentId/:revision/:preview',
    'webinars/:articleId': 'webinars/:articleId',
    'ebooks/:articleId': 'ebooks/:articleId',
    'case_studies/:articleId': 'case-studies/:articleId',
    ':pageId': ':pageId',
    ebook: 'ebooks',
    webinar: 'webinars',
    main: '',
    '*': '*',
  };
};

const getPageSlug = (page: WPSimplePage<WPAcfBasic> | WPSimpleArticle<WPAcfBasic>) => (page.acf && page.acf.lang_slug ? page.acf.lang_slug : page.slug);

const getDefaultLanguageSlugs = (items: Record<string, (WPSimplePage<WPAcfBasic> | WPSimpleArticle<WPAcfBasic>)[]>) => ({
  ...Object.keys(items).reduce((accumulator, item) => {
    const pageOrPost = items[item] as (WPSimplePage<WPAcfBasic> | WPSimpleArticle<WPAcfBasic>)[];
    if (pageOrPost && Array.isArray(pageOrPost) && pageOrPost.length === 1 && pageOrPost[0].wpml_translations) {
      const firstRecord = pageOrPost[0];
      const pageSlug = getPageSlug(firstRecord);

      return {
        ...accumulator,
        [pageSlug]: firstRecord.slug,
        ...(pageSlug === 'main' && { '': firstRecord.slug }),
      };
    }

    return accumulator;
  }, {}),
  ...getTranslatedNestedPages(items),
  ...getAdditionalLanguageSlugs(),
});

const getTranslatedNestedPages = (items: Record<string, any>, language?: LanguageTypes) => {
  const nested = {
    'join_us/:offerId': 'join_us/:offerId',
  };
  return Object.keys(items).reduce((accumulator, item) => {
    const pageOrPost = items[item] as (WPSimplePage<WPAcfBasic> | WPSimpleArticle<WPAcfBasic>)[];

    if (pageOrPost && Array.isArray(pageOrPost) && pageOrPost.length === 1 && pageOrPost[0].wpml_translations) {
      const firstRecord = pageOrPost[0];
      const pageSlug = getPageSlug(firstRecord);
      const translation = firstRecord.wpml_translations && firstRecord.wpml_translations.find(lang => lang.language === language);

      const translationX = Object.keys(nested)
        .map(key => {
          // @ts-ignore
          if (nested[key] && nested[key].split('/')[0] === pageSlug) {
            // @ts-ignore
            return { [nested[key]]: `${translation?.slug || firstRecord?.slug}/${nested[key].split('/')[1]}` || '' };
          }
          return undefined;
        })
        .filter(value => !!value);

      return {
        ...accumulator,
        ...(translationX && translationX[0] && translationX[0] !== undefined && translationX[0]),
      };
    }

    return accumulator;
  }, {});
};

const getLanguageSlugs = (items: Record<string, any>, language: LanguageTypes) => ({
  ...Object.keys(items).reduce((accumulator, item) => {
    const pageOrPost = items[item] as (WPSimplePage<WPAcfBasic> | WPSimpleArticle<WPAcfBasic>)[];

    if (pageOrPost && Array.isArray(pageOrPost) && pageOrPost.length === 1 && pageOrPost[0].wpml_translations) {
      const firstRecord = pageOrPost[0];
      const pageSlug = getPageSlug(firstRecord);
      const translation = firstRecord.wpml_translations && firstRecord.wpml_translations.find(lang => lang.language === language);

      return {
        ...accumulator,
        [pageSlug]: translation?.slug || '',
      };
    }

    return accumulator;
  }, {}),
  ...getTranslatedNestedPages(items, language),
  ...getAdditionalLanguageSlugs(),
});

const translatedLanguages = (items: Record<string, (WPSimplePage<WPAcfBasic> | WPSimpleArticle<WPAcfBasic>)[]>) =>
  config.languages.reduce(
    (accumulator, language) => ({
      ...accumulator,
      [language]: getLanguageSlugs(items, language),
    }),
    getLanguages(),
  );

export const getTranslatedRoutes = (items: Record<string, (WPSimplePage<WPAcfBasic> | WPSimpleArticle<WPAcfBasic>)[]>, language: LanguageTypes) => {
  return {
    ...translatedLanguages(items),
    [language]: getDefaultLanguageSlugs(items),
  };
};

const getDefaultGlobalCache = () => ({
  languageRoutes: {},
  pages: [],
  items: {},
});

export const useCachedPageRoutes = (language: LanguageTypes) => {
  const DEBUG = false;
  const [data, setData] = useState<CachedDataProps>({
    pages: null,
    languageRoutes: null,
  });
  const { data: pages, loading } = useAllPages<WPSimplePage<WPAcfBasic>>({ per_page: 50 });

  DEBUG && console.log('Routes component', { pages, loading });

  const prepareData = (pages: WPSimplePage<WPAcfBasic>[]) => {
    DEBUG && console.log('routes - Prepare data');
    const transformedPages = pages.map(page => {
      const title = page.yoast_meta.find(meta => meta.property === 'og:title');
      page.yoast_title = title ? title.content : page.yoast_title;
      return page;
    });

    const items = {
      [`pages:${language}`]: transformedPages,
      ...transformedPages.reduce(
        (accumulator, page) => ({
          ...accumulator,
          [`page:${page.slug}:${language}`]: [page],
        }),
        {},
      ),
    };

    const languageRoutes = getTranslatedRoutes(items, language);

    DEBUG && console.log('translated routes', languageRoutes);

    return { transformedPages, languageRoutes, items };
  };

  useEffect(() => {
    DEBUG && console.log('routes - data changes');
    if (pages) {
      const { transformedPages, languageRoutes, items } = prepareData(pages);

      DEBUG &&
        console.log('routes - data equals?', {
          pagesEqual: isEqual(transformedPages, window?.globalCache?.pages),
          languageRoutesEqual: isEqual(languageRoutes, window?.globalCache?.languageRoutes),
        });

      if (!window?.globalCache) {
        // @ts-ignore
        window.globalCache = getDefaultGlobalCache();
      }

      // @ts-ignore
      window.globalCache.languageRoutes = languageRoutes;
      window.globalCache.pages = transformedPages;
      window.globalCache.items = items;

      DEBUG && console.log('routes - new cache');
      setData({
        pages: transformedPages || null,
        languageRoutes: languageRoutes || null,
      });
    }
    // eslint-disable-next-line
  }, [pages, language]);

  return { ...data, loading };
};

export const useCachedPostRoutes = () => {
  const shouldFetchPosts = !hasAcceptablePostRoutes();
  const { data: posts, loading } = usePages<WPSimplePage<WPAcfBasic>>({ per_page: 100, skip: !shouldFetchPosts });

  if (!window?.globalCache) {
    // @ts-ignore
    window.globalCache = {
      postRoutes: {},
      languageRoutes: {},
      pages: [],
    };
  }

  if (posts) {
    posts.forEach(post => {
      window.globalCache.postRoutes[post.slug] = post.acf && post.acf.lang_slug ? post.acf.lang_slug : post.slug;
    });
  }

  return { acceptableRoutes: getAcceptablePostRoutes() || null, posts, loading };
};

export const isWebcache = (windowObject = window) => {
  return windowObject.location.pathname === '/search' && windowObject.location.hostname.includes('webcache.googleusercontent.com');
};

export const getCachedUrl = () => {
  const urlObject = new URLSearchParams(window.location.search);
  const params = urlObject.get('q') || '';
  const [, path = ''] = params.split('testarmy.com');
  return path.trim();
};
